/**
 * Public dashboard-side password reset page (linked from forgot-password
 * emails). Reads the token from `?token=...` and lets the user set a new
 * password. Same backend endpoint (`POST /api/auth/reset-password`) as the
 * staff portal version.
 */
import { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { PasswordInput } from '@/components/ui/password-input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Lock } from 'lucide-react';
import { appReset, formatApiErrorDetail } from '@/lib/appApi';

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (pw.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (pw !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setBusy(true);
    try {
      await appReset(token, pw);
      toast.success('Password updated — please sign in');
      navigate('/portal-signin');
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || 'Reset failed');
    } finally { setBusy(false); }
  }

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-white to-emerald-50 px-4">
        <Card className="w-full max-w-sm shadow-sm border-0">
          <CardContent className="p-7 text-center space-y-4">
            <h1 className="text-xl font-bold text-gray-900">Invalid reset link</h1>
            <p className="text-sm text-gray-500">This URL is missing the reset token. Make sure you opened the link from your email.</p>
            <Link to="/portal-signin" className="text-sm text-emerald-700 hover:text-emerald-800 font-medium">← Back to sign in</Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-white to-emerald-50 px-4">
      <Card className="w-full max-w-sm shadow-sm border-0" data-testid="reset-password-page">
        <CardContent className="p-7">
          <div className="flex flex-col items-center text-center mb-6">
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mb-3">
              <Lock className="h-5 w-5 text-emerald-700" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Set a new password</h1>
            <p className="text-sm text-gray-500 mt-1">Pick something memorable but at least 8 characters.</p>
          </div>
          <form className="space-y-3" onSubmit={submit}>
            <PasswordInput
              placeholder="New password (≥ 8 chars)" required minLength={8} defaultVisible
              value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password-new"
            />
            <PasswordInput
              placeholder="Confirm new password" required minLength={8} defaultVisible
              value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-password-confirm"
            />
            <Button type="submit" disabled={busy} className="w-full" data-testid="reset-password-submit">
              {busy ? 'Updating…' : 'Update password'}
            </Button>
          </form>
          <p className="mt-5 text-center">
            <Link to="/portal-signin" className="text-xs text-gray-500 hover:text-gray-700">← Back to sign in</Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
